import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../config/firebase';
import { collection, query, getDocs } from 'firebase/firestore';
import { BarChart3, TrendingUp, Users, Building2, Calendar, ArrowUp, ArrowDown, Briefcase, UserCheck, Download, ChevronRight, ChevronDown, Filter, MapPin } from 'lucide-react';
import { format, startOfMonth, endOfMonth, parseISO, isValid, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useUserStore } from '../stores/userStore';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { useThemeStore } from '../stores/themeStore';
import OverviewChart from '../components/dashboard/OverviewChart';

interface UserStats {
  userId: string;
  userName: string;
  email: string;
  offersCollected: number;
  offersPlaced: number;
  pmsmpOffers: number;
  newCompanies: number;
  eventsCreated: number;
  beneficiairesAdded: number;
  employedBeneficiaires: number;
  totalBeneficiaires: number;
  placementRate: number;
  eventProposals: number;
  monthlyTrend: 'up' | 'down' | 'stable';
  jobboardTransmissions: number;
  prospectionTransmissions: number;
  directCVTransmissions: number;
}

interface MonthlyStats {
  month: string;
  offersCollected: number;
  offersPlaced: number;
  pmsmpOffers: number;
  newCompanies: number;
  eventsCreated: number;
  employedBeneficiaires: number;
  totalBeneficiaires: number;
  employmentRate: number;
  jobboardTransmissions: number;
  prospectionTransmissions: number;
  directCVTransmissions: number;
}

interface AgencyStats {
  agencyId: string;
  agencyName: string;
  jobboardTransmissions: number;
  prospectionTransmissions: number;
  directCVTransmissions: number;
  pmsmpOffers: number;
  totalActions: number;
}

export default function Monitoring() {
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [agencyStats, setAgencyStats] = useState<AgencyStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { userData } = useUserStore();
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const { darkMode } = useThemeStore();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [tempSelectedUsers, setTempSelectedUsers] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [showChart, setShowChart] = useState(true);
  const [showAgencyStats, setShowAgencyStats] = useState(true);
  const [showUserStats, setShowUserStats] = useState(true);
  const [visibleColumns, setVisibleColumns] = useState({
    email: true,
    offersCollected: true,
    offersPlaced: true,
    jobboardTransmissions: true,
    prospectionTransmissions: true,
    directCVTransmissions: true,
    pmsmpOffers: true,
    newCompanies: true,
    eventsCreated: true,
    eventProposals: true,
    beneficiairesAdded: true,
    employedBeneficiaires: true,
    placementRate: true,
    monthlyTrend: true
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      // Check user permissions
      const hasAccess = userData && (
        userData.role === 'admin' || 
        userData.role === 'analyst' || 
        userData.role === 'manager' ||
        userData.email === 'slucas@anthea-rh.com' ||
        userData.email === 'vgarau@anthea-rh.com'
      );

      setHasPermission(!!hasAccess);

      if (!hasAccess) {
        setError("Vous n'avez pas les permissions nécessaires pour accéder à cette page.");
        setIsLoading(false);
        return;
      }

      loadStats();
    });

    return () => unsubscribe();
  }, [navigate, userData]);

  const loadStats = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log("Loading monitoring stats...");
      
      // Charger tous les utilisateurs (sans filtrer les supprimés)
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      // Créer une Map pour stocker les utilisateurs uniques par email
      const usersMap = new Map();
      
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        
        // Ne pas inclure les utilisateurs supprimés
        if (userData.deleted === true) {
          return;
        }
        
        // Utiliser l'email comme clé unique pour éviter les doublons
        if (userData.email && !usersMap.has(userData.email)) {
          if (userData.email === 'slucas@anthea-rh.com' || userData.email === 'vgarau@anthea-rh.com' || userData.id === 'a0yoYjd1EaO2jrZf42ZIo7583cf2') {
            usersMap.set(userData.email, {
              id: doc.id,
              ...userData,
              role: 'admin',
              lastLogin: userData.lastLogin?.toDate(),
              createdAt: userData.createdAt?.toDate(),
              deletedAt: userData.deletedAt?.toDate(),
              initialPassword: userData.initialPassword,
              firebaseUid: userData.uid,
              loginHistory: userData.loginHistory?.map((login: any) => ({
                date: login.date.toDate(),
                count: login.count
              })) || []
            });
          } else {
            usersMap.set(userData.email, {
              id: doc.id,
              ...userData,
              lastLogin: userData.lastLogin?.toDate(),
              createdAt: userData.createdAt?.toDate(),
              deletedAt: userData.deletedAt?.toDate(),
              initialPassword: userData.initialPassword,
              firebaseUid: userData.uid,
              loginHistory: userData.loginHistory?.map((login: any) => ({
                date: login.date.toDate(),
                count: login.count
              })) || []
            });
          }
        }
      });
      
      const users = Array.from(usersMap.values());
      console.log(`Loaded ${users.length} unique users`);

      // Charger les offres
      const offersSnapshot = await getDocs(collection(db, 'offers'));
      const offers = offersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Charger les entreprises
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companies = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Charger les événements
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const events = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Charger les bénéficiaires
      const beneficiairesSnapshot = await getDocs(collection(db, 'beneficiaires'));
      const beneficiaires = beneficiairesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Charger les agences
      const agenciesSnapshot = await getDocs(collection(db, 'agencies'));
      const agencies = agenciesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculer les dates pour le mois actuel et le mois précédent
      const now = new Date();
      const currentMonthStart = startOfMonth(now);
      const currentMonthEnd = endOfMonth(now);
      const previousMonthStart = startOfMonth(subMonths(now, 1));
      const previousMonthEnd = endOfMonth(subMonths(now, 1));

      // Calculer les statistiques par utilisateur
      const stats = users.map(user => {
        const userOffers = offers.filter(o => o.userId === user.id);
        const userCompanies = companies.filter(c => c.userId === user.id);
        const userEvents = events.filter(e => e.userId === user.id);
        const userBeneficiaires = beneficiaires.filter(b => b.userId === user.id);
        const employedBeneficiaires = userBeneficiaires.filter(b => b.employed === true);

        const offersCollected = userOffers.length;
        const offersPlaced = userOffers.filter(o => o.status === 'filled').length;

        // Calcul du taux de placement basé sur les bénéficiaires en emploi
        const placementRate = userBeneficiaires.length > 0
          ? Math.round((employedBeneficiaires.length / userBeneficiaires.length) * 100)
          : 0;

        const userPmsmpOffers = userOffers.filter(o => o.type === 'PMSMP').length;

        // Calculer les transmissions par catégorie
        const jobboardTransmissions = userOffers.filter((o: any) => o.transmissionStatus === 'jobboard').length;
        const prospectionTransmissions = userOffers.filter((o: any) => o.transmissionStatus === 'prospection').length;
        const directCVTransmissions = userOffers.filter((o: any) => o.transmissionStatus === 'direct').length;

        // Calculer le nombre de bénéficiaires à qui des événements ont été proposés
        const eventProposals = userEvents.reduce((total, event: any) => {
          return total + (event.invitedBeneficiaires?.length || 0);
        }, 0);

        // Calculer les offres collectées pour le mois actuel et le mois précédent
        const currentMonthOffers = userOffers.filter(o => {
          const offerDate = new Date(o.createdAt);
          return offerDate >= currentMonthStart && offerDate <= currentMonthEnd;
        }).length;

        const previousMonthOffers = userOffers.filter(o => {
          const offerDate = new Date(o.createdAt);
          return offerDate >= previousMonthStart && offerDate <= previousMonthEnd;
        }).length;

        // Déterminer la tendance basée sur la comparaison des offres collectées
        let monthlyTrend: 'up' | 'down' | 'stable' = 'stable';
        if (previousMonthOffers > 0) {
          const percentageChange = ((currentMonthOffers - previousMonthOffers) / previousMonthOffers) * 100;
          if (percentageChange > 10) {
            monthlyTrend = 'up';
          } else if (percentageChange < -10) {
            monthlyTrend = 'down';
          }
        } else if (currentMonthOffers > 0) {
          monthlyTrend = 'up';
        }

        return {
          userId: user.id,
          userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Utilisateur sans nom',
          email: user.email || '',
          offersCollected,
          offersPlaced,
          pmsmpOffers: userPmsmpOffers,
          newCompanies: userCompanies.length,
          eventsCreated: userEvents.length,
          beneficiairesAdded: userBeneficiaires.length,
          employedBeneficiaires: employedBeneficiaires.length,
          totalBeneficiaires: userBeneficiaires.length,
          placementRate,
          eventProposals,
          monthlyTrend,
          jobboardTransmissions,
          prospectionTransmissions,
          directCVTransmissions
        };
      });

      // Calculer les statistiques mensuelles
      const monthStart = currentMonthStart;
      const monthEnd = currentMonthEnd;

      const monthlyEmployedBeneficiaires = beneficiaires.filter(b => b.employed === true).length;
      const monthlyTotalBeneficiaires = beneficiaires.length;
      const employmentRate = monthlyTotalBeneficiaires > 0
        ? Math.round((monthlyEmployedBeneficiaires / monthlyTotalBeneficiaires) * 100)
        : 0;

      const monthlyStats: MonthlyStats = {
        month: format(now, 'MMMM yyyy', { locale: fr }),
        offersCollected: offers.filter(o => {
          if (!o.createdAt) return true;
          const offerDate = new Date(o.createdAt);
          return offerDate >= monthStart && offerDate <= monthEnd;
        }).length,
        offersPlaced: offers.filter(o => {
          if (!o.createdAt) return o.status === 'filled';
          const offerDate = new Date(o.createdAt);
          return offerDate >= monthStart && offerDate <= monthEnd && o.status === 'filled';
        }).length,
        pmsmpOffers: offers.filter(o => o.type === 'PMSMP').length,
        newCompanies: companies.length,
        eventsCreated: events.length,
        employedBeneficiaires: monthlyEmployedBeneficiaires,
        totalBeneficiaires: monthlyTotalBeneficiaires,
        employmentRate: employmentRate,
        jobboardTransmissions: offers.filter((o: any) => o.transmissionStatus === 'jobboard').length,
        prospectionTransmissions: offers.filter((o: any) => o.transmissionStatus === 'prospection').length,
        directCVTransmissions: offers.filter((o: any) => o.transmissionStatus === 'direct').length
      };

      // Calculer les statistiques par agence
      const agencyStatsData: AgencyStats[] = agencies.map((agency: any) => {
        // Trouver les bénéficiaires de cette agence
        const agencyBeneficiaires = beneficiaires.filter((b: any) => b.agencyId === agency.id);
        const beneficiaireIds = new Set(agencyBeneficiaires.map((b: any) => b.id));

        // Compter les offres par critère de transmission
        let jobboardCount = 0;
        let prospectionCount = 0;
        let cvDirectCount = 0;
        let pmsmpCount = 0;

        offers.forEach((offer: any) => {
          const hasBeneficiaireFromAgency = offer.candidates?.some(
            (candidate: any) => beneficiaireIds.has(candidate.beneficiaireId)
          );

          if (hasBeneficiaireFromAgency) {
            if (offer.transmissionStatus === 'jobboard') {
              jobboardCount++;
            } else if (offer.transmissionStatus === 'prospection') {
              prospectionCount++;
            } else if (offer.transmissionStatus === 'direct') {
              cvDirectCount++;
            }

            if (offer.type?.toLowerCase().includes('pmsmp')) {
              pmsmpCount++;
            }
          }
        });

        return {
          agencyId: agency.id,
          agencyName: agency.name || 'Agence sans nom',
          jobboardTransmissions: jobboardCount,
          prospectionTransmissions: prospectionCount,
          directCVTransmissions: cvDirectCount,
          pmsmpOffers: pmsmpCount,
          totalActions: jobboardCount + prospectionCount + cvDirectCount + pmsmpCount
        };
      }).sort((a, b) => b.totalActions - a.totalActions);

      setUserStats(stats);
      setMonthlyStats([monthlyStats]);
      setAgencyStats(agencyStatsData);
      setIsLoading(false);
      console.log("Monitoring stats loaded successfully");
    } catch (error) {
      console.error('Error loading monitoring stats:', error);
      setError('Erreur lors du chargement des statistiques');
      setIsLoading(false);
    }
  };

  const handleExportToExcel = () => {
    try {
      // Préparer les données pour l'export (utiliser les stats filtrées)
      const statsToExport = selectedUsers.length > 0 ? filteredUserStats : userStats;
      const userStatsData = statsToExport.map(stat => ({
        'Utilisateur': stat.userName,
        'Email': stat.email,
        'Offres collectées': stat.offersCollected,
        'Offres pourvues': stat.offersPlaced,
        'PMSMP': stat.pmsmpOffers,
        'Transmission offres jobboard': stat.jobboardTransmissions,
        'Transmission offres prospection': stat.prospectionTransmissions,
        'Transmission CV direct entreprises': stat.directCVTransmissions,
        'Nouvelles entreprises': stat.newCompanies,
        'Événements créés': stat.eventsCreated,
        'Invitations événements': stat.eventProposals,
        'Bénéficiaires ajoutés': stat.beneficiairesAdded,
        'Bénéficiaires en emploi': `${stat.employedBeneficiaires} / ${stat.totalBeneficiaires}`,
        'Taux de placement': `${stat.placementRate}%`
      }));

      const monthlyStatsData = monthlyStats.map(stat => ({
        'Mois': stat.month,
        'Offres collectées': stat.offersCollected,
        'Offres pourvues': stat.offersPlaced,
        'PMSMP': stat.pmsmpOffers,
        'Nouvelles entreprises': stat.newCompanies,
        'Événements créés': stat.eventsCreated,
        'Bénéficiaires en emploi': `${stat.employedBeneficiaires} / ${stat.totalBeneficiaires}`,
        'Taux d\'emploi': `${stat.employmentRate}%`
      }));

      // Créer un nouveau classeur Excel
      const wb = XLSX.utils.book_new();

      // Ajouter la feuille des statistiques par utilisateur
      const wsUsers = XLSX.utils.json_to_sheet(userStatsData);
      XLSX.utils.book_append_sheet(wb, wsUsers, 'Statistiques par utilisateur');

      // Ajouter la feuille des statistiques mensuelles
      const wsMonthly = XLSX.utils.json_to_sheet(monthlyStatsData);
      XLSX.utils.book_append_sheet(wb, wsMonthly, 'Statistiques mensuelles');

      // Générer le nom du fichier avec la date actuelle
      const fileName = `monitoring_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;

      // Télécharger le fichier
      XLSX.writeFile(wb, fileName);

      toast.success('Export Excel réussi !');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const handleExportAgencyStats = () => {
    try {
      const agencyStatsData = agencyStats.map(stat => ({
        'Agence': stat.agencyName,
        'Transmission offres jobboard': stat.jobboardTransmissions,
        'Transmission offres prospection': stat.prospectionTransmissions,
        'Transmission CV direct entreprises': stat.directCVTransmissions,
        'PMSMP': stat.pmsmpOffers,
        'Total actions': stat.totalActions
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(agencyStatsData);

      const colWidths = [
        { wch: 30 },
        { wch: 25 },
        { wch: 28 },
        { wch: 32 },
        { wch: 15 },
        { wch: 15 }
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Statistiques par agence');

      const fileName = `statistiques_agences_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success('Export Excel des statistiques par agence réussi !');
    } catch (error) {
      console.error('Error exporting agency stats to Excel:', error);
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const toggleUserExpand = (userId: string) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
    }
  };

  if (!hasPermission) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <div className={`${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-700'} rounded-xl p-6 text-center max-w-lg`}>
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-red-300' : 'text-red-700'} mb-2`}>Accès refusé</h2>
          <p className={darkMode ? 'text-red-300' : 'text-red-600'}>
            Vous n'avez pas les permissions nécessaires pour accéder à cette page. 
            Veuillez contacter un administrateur si vous pensez qu'il s'agit d'une erreur.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anthea-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 ${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-600'} rounded-xl`}>
        <p className={darkMode ? 'text-red-300' : 'text-red-600'}>{error}</p>
      </div>
    );
  }

  const filteredUserStats = isFilterActive && selectedUsers.length > 0
    ? userStats.filter(stat => selectedUsers.includes(stat.userId))
    : userStats;

  const toggleUserSelection = (userId: string) => {
    setTempSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    setTempSelectedUsers(userStats.map(stat => stat.userId));
  };

  const clearUserSelection = () => {
    setTempSelectedUsers([]);
  };

  const applyFilter = () => {
    setSelectedUsers(tempSelectedUsers);
    setIsFilterActive(tempSelectedUsers.length > 0);
    setShowFilters(false);
  };

  const clearFilter = () => {
    setSelectedUsers([]);
    setTempSelectedUsers([]);
    setIsFilterActive(false);
  };

  const openFilters = () => {
    setTempSelectedUsers(selectedUsers);
    setShowFilters(true);
  };

  const toggleColumnVisibility = (column: keyof typeof visibleColumns) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Monitoring</h1>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {isFilterActive ? (
            <button
              onClick={clearFilter}
              className={`flex items-center px-4 py-2 ${darkMode ? 'bg-red-700 text-white' : 'bg-red-600 text-white'} rounded-xl hover:${darkMode ? 'bg-red-600' : 'bg-red-700'} transition-colors`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Désactiver le filtre ({selectedUsers.length})
            </button>
          ) : (
            <button
              onClick={openFilters}
              className={`flex items-center px-4 py-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700'} rounded-xl hover:${darkMode ? 'bg-gray-600' : 'bg-gray-300'} transition-colors`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtrer
            </button>
          )}
          <button
            onClick={handleExportToExcel}
            className={`hidden md:flex items-center px-4 py-2 ${darkMode ? 'bg-green-700 text-white' : 'bg-green-600 text-white'} rounded-xl hover:${darkMode ? 'bg-green-600' : 'bg-green-700'} transition-colors`}
          >
            <Download className="w-4 h-4 mr-2" />
            Exporter en Excel
          </button>
          <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
            Dernière mise à jour : {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: fr })}
          </div>
          <button
            onClick={loadStats}
            className="px-4 py-2 bg-gradient-anthea text-white rounded-xl hover:opacity-90 transition-opacity"
          >
            Actualiser
          </button>
        </div>
      </div>

      {/* Panneau de filtres */}
      {showFilters && (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Filtrer par utilisateur
            </h3>
            <div className="flex gap-2">
              <button
                onClick={selectAllUsers}
                className={`text-sm px-3 py-1 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}
              >
                Tout sélectionner
              </button>
              <button
                onClick={clearUserSelection}
                className={`text-sm px-3 py-1 ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'} transition-colors`}
              >
                Tout effacer
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {userStats.map((stat) => (
              <label
                key={stat.userId}
                className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                  tempSelectedUsers.includes(stat.userId)
                    ? darkMode ? 'bg-blue-900 border-2 border-blue-500' : 'bg-blue-50 border-2 border-blue-500'
                    : darkMode ? 'bg-gray-700 hover:bg-gray-600 border-2 border-transparent' : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                }`}
              >
                <input
                  type="checkbox"
                  checked={tempSelectedUsers.includes(stat.userId)}
                  onChange={() => toggleUserSelection(stat.userId)}
                  className="mr-3 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stat.userName}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {stat.email}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={() => setShowFilters(false)}
              className={`px-4 py-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700'} rounded-xl hover:${darkMode ? 'bg-gray-600' : 'bg-gray-300'} transition-colors`}
            >
              Annuler
            </button>
            <button
              onClick={applyFilter}
              disabled={tempSelectedUsers.length === 0}
              className={`px-4 py-2 rounded-xl transition-colors ${
                tempSelectedUsers.length === 0
                  ? darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-anthea text-white hover:opacity-90'
              }`}
            >
              Appliquer le filtre ({tempSelectedUsers.length})
            </button>
          </div>
        </div>
      )}

      {/* Statistiques globales du mois */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
        <div className="p-6">
          <h2 className={`text-lg font-semibold mb-6 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <BarChart3 className="w-5 h-5 mr-2 text-anthea-blue" />
            Vue d'ensemble - {monthlyStats[0]?.month}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className={`${darkMode ? 'bg-blue-900' : 'bg-blue-50'} rounded-xl p-3 md:p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs md:text-sm ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Offres collectées</p>
                  <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>{monthlyStats[0]?.offersCollected}</p>
                </div>
                <Briefcase className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-blue-400' : 'text-blue-400'}`} />
              </div>
            </div>
            <div className={`${darkMode ? 'bg-green-900' : 'bg-green-50'} rounded-xl p-3 md:p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs md:text-sm ${darkMode ? 'text-green-300' : 'text-green-600'}`}>Offres pourvues</p>
                  <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{monthlyStats[0]?.offersPlaced}</p>
                </div>
                <TrendingUp className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-green-400' : 'text-green-400'}`} />
              </div>
            </div>
            <div className={`${darkMode ? 'bg-purple-900' : 'bg-purple-50'} rounded-xl p-3 md:p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs md:text-sm ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>PMSMP</p>
                  <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>{monthlyStats[0]?.pmsmpOffers}</p>
                </div>
                <Briefcase className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-purple-400' : 'text-purple-400'}`} />
              </div>
            </div>
            <div className={`${darkMode ? 'bg-yellow-900' : 'bg-yellow-50'} rounded-xl p-3 md:p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs md:text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>Nouvelles entreprises</p>
                  <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>{monthlyStats[0]?.newCompanies}</p>
                </div>
                <Building2 className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-yellow-400' : 'text-yellow-400'}`} />
              </div>
            </div>
            <div className={`${darkMode ? 'bg-orange-900' : 'bg-orange-50'} rounded-xl p-3 md:p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs md:text-sm ${darkMode ? 'text-orange-300' : 'text-orange-600'}`}>Événements créés</p>
                  <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>{monthlyStats[0]?.eventsCreated}</p>
                </div>
                <Calendar className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-orange-400' : 'text-orange-400'}`} />
              </div>
            </div>
            <div className="col-span-2 md:col-span-1 bg-teal-50 rounded-xl p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs md:text-sm ${darkMode ? 'text-teal-300' : 'text-teal-600'}`}>Taux d'emploi</p>
                  <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>{monthlyStats[0]?.employmentRate}%</p>
                  <p className={`text-xs ${darkMode ? 'text-teal-300' : 'text-teal-600'}`}>{monthlyStats[0]?.employedBeneficiaires} / {monthlyStats[0]?.totalBeneficiaires}</p>
                </div>
                <UserCheck className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-teal-400' : 'text-teal-400'}`} />
              </div>
            </div>
          </div>

          {/* Section Transmissions */}
          <div className="mt-6">
            <h3 className={`text-md font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Catégories de transmission
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`${darkMode ? 'bg-cyan-900' : 'bg-cyan-50'} rounded-xl p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs md:text-sm ${darkMode ? 'text-cyan-300' : 'text-cyan-600'}`}>Transmission offres jobboard</p>
                    <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>{monthlyStats[0]?.jobboardTransmissions || 0}</p>
                  </div>
                  <TrendingUp className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-cyan-400' : 'text-cyan-400'}`} />
                </div>
              </div>
              <div className={`${darkMode ? 'bg-sky-900' : 'bg-sky-50'} rounded-xl p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs md:text-sm ${darkMode ? 'text-sky-300' : 'text-sky-600'}`}>Transmission offres prospection</p>
                    <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-sky-300' : 'text-sky-700'}`}>{monthlyStats[0]?.prospectionTransmissions || 0}</p>
                  </div>
                  <Building2 className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-sky-400' : 'text-sky-400'}`} />
                </div>
              </div>
              <div className={`${darkMode ? 'bg-emerald-900' : 'bg-emerald-50'} rounded-xl p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs md:text-sm ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Transmission CV direct entreprises</p>
                    <p className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>{monthlyStats[0]?.directCVTransmissions || 0}</p>
                  </div>
                  <Users className={`w-6 h-6 md:w-8 md:h-8 ${darkMode ? 'text-emerald-400' : 'text-emerald-400'}`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Graphique Vue d'ensemble */}
      {monthlyStats[0] && (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
              <BarChart3 className="w-5 h-5 mr-2 text-anthea-blue" />
              Graphique de la Vue d'ensemble
            </h3>
            <button
              onClick={() => setShowChart(!showChart)}
              className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
                showChart
                  ? darkMode ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
                  : darkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              title={showChart ? 'Cacher le graphique' : 'Afficher le graphique'}
            >
              <span className="text-xl font-bold">{showChart ? '-' : '+'}</span>
            </button>
          </div>
          {showChart && (
            <OverviewChart
              title={`Vue d'ensemble - ${monthlyStats[0]?.month}`}
              data={[
                { label: 'Offres collectées', value: monthlyStats[0]?.offersCollected || 0, color: '#3b82f6' },
                { label: 'Offres pourvues', value: monthlyStats[0]?.offersPlaced || 0, color: '#10b981' },
                { label: 'PMSMP', value: monthlyStats[0]?.pmsmpOffers || 0, color: '#a855f7' },
                { label: 'Nouvelles entreprises', value: monthlyStats[0]?.newCompanies || 0, color: '#eab308' },
                { label: 'Événements créés', value: monthlyStats[0]?.eventsCreated || 0, color: '#f97316' },
                { label: 'Jobboard', value: monthlyStats[0]?.jobboardTransmissions || 0, color: '#06b6d4' },
                { label: 'Prospection', value: monthlyStats[0]?.prospectionTransmissions || 0, color: '#0ea5e9' },
                { label: 'CV direct', value: monthlyStats[0]?.directCVTransmissions || 0, color: '#059669' },
                { label: 'Bénéficiaires en emploi', value: monthlyStats[0]?.employedBeneficiaires || 0, color: '#14b8a6' }
              ]}
            />
          )}
        </div>
      )}

      {/* Statistiques par agence */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <MapPin className="w-5 h-5 mr-2 text-anthea-blue" />
              Performance par agence
            </h2>
            <div className="flex gap-2">
              <button
                onClick={handleExportAgencyStats}
                className={`flex items-center px-4 py-2 ${darkMode ? 'bg-green-700 text-white' : 'bg-green-600 text-white'} rounded-xl hover:${darkMode ? 'bg-green-600' : 'bg-green-700'} transition-colors`}
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </button>
              <button
                onClick={() => setShowAgencyStats(!showAgencyStats)}
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
                  showAgencyStats
                    ? darkMode ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
                    : darkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
                title={showAgencyStats ? 'Masquer' : 'Afficher'}
              >
                <span className="text-xl font-bold">{showAgencyStats ? '-' : '+'}</span>
              </button>
            </div>
          </div>

          {showAgencyStats && (
            <div className="overflow-x-auto">
              <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      Agence
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      Transmission offres jobboard
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      Transmission offres prospection
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      Transmission CV direct entreprises
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      PMSMP
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      Total actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`${darkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}`}>
                  {agencyStats.map((stat) => (
                    <tr key={stat.agencyId} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-medium">{stat.agencyName}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-cyan-600">{stat.jobboardTransmissions}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-sky-600">{stat.prospectionTransmissions}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-emerald-600">{stat.directCVTransmissions}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-purple-600">{stat.pmsmpOffers}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className={`text-sm font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{stat.totalActions}</div>
                      </td>
                    </tr>
                  ))}
                  {agencyStats.length === 0 && (
                    <tr>
                      <td colSpan={6} className={`px-6 py-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Aucune agence trouvée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Statistiques par utilisateur - Version desktop */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden hidden md:block`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Users className="w-5 h-5 mr-2 text-anthea-blue" />
              Performance par utilisateur
            </h2>
            <button
              onClick={() => setShowUserStats(!showUserStats)}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
                showUserStats
                  ? darkMode ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
                  : darkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              title={showUserStats ? 'Masquer' : 'Afficher'}
            >
              <span className="text-xl font-bold">{showUserStats ? '-' : '+'}</span>
            </button>
          </div>
          {showUserStats && (
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    Utilisateur
                  </th>
                  {visibleColumns.email && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('email')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Email
                    </th>
                  )}
                  {!visibleColumns.email && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('email')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Email"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.offersCollected && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('offersCollected')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Offres collectées
                    </th>
                  )}
                  {!visibleColumns.offersCollected && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('offersCollected')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Offres collectées"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.offersPlaced && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('offersPlaced')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Offres pourvues
                    </th>
                  )}
                  {!visibleColumns.offersPlaced && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('offersPlaced')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Offres pourvues"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.jobboardTransmissions && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('jobboardTransmissions')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Offres jobboard
                    </th>
                  )}
                  {!visibleColumns.jobboardTransmissions && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('jobboardTransmissions')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Offres jobboard"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.prospectionTransmissions && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('prospectionTransmissions')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Offres prospection
                    </th>
                  )}
                  {!visibleColumns.prospectionTransmissions && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('prospectionTransmissions')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Offres prospection"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.directCVTransmissions && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('directCVTransmissions')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      CV direct
                    </th>
                  )}
                  {!visibleColumns.directCVTransmissions && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('directCVTransmissions')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="CV direct"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.pmsmpOffers && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('pmsmpOffers')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      PMSMP
                    </th>
                  )}
                  {!visibleColumns.pmsmpOffers && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('pmsmpOffers')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="PMSMP"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.newCompanies && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('newCompanies')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Nouvelles entreprises
                    </th>
                  )}
                  {!visibleColumns.newCompanies && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('newCompanies')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Nouvelles entreprises"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.eventsCreated && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('eventsCreated')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Événements créés
                    </th>
                  )}
                  {!visibleColumns.eventsCreated && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('eventsCreated')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Événements créés"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.eventProposals && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('eventProposals')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Invitations événements
                    </th>
                  )}
                  {!visibleColumns.eventProposals && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('eventProposals')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Invitations événements"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.beneficiairesAdded && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('beneficiairesAdded')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Bénéficiaires ajoutés
                    </th>
                  )}
                  {!visibleColumns.beneficiairesAdded && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('beneficiairesAdded')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Bénéficiaires ajoutés"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.employedBeneficiaires && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('employedBeneficiaires')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Bénéficiaires en emploi
                    </th>
                  )}
                  {!visibleColumns.employedBeneficiaires && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('employedBeneficiaires')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Bénéficiaires en emploi"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.placementRate && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('placementRate')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Taux de placement
                    </th>
                  )}
                  {!visibleColumns.placementRate && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('placementRate')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Taux de placement"
                      >
                        +
                      </button>
                    </th>
                  )}
                  {visibleColumns.monthlyTrend && (
                    <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      <button
                        onClick={() => toggleColumnVisibility('monthlyTrend')}
                        className="mr-2 text-red-500 hover:text-red-700 font-bold"
                      >
                        -
                      </button>
                      Tendance
                    </th>
                  )}
                  {!visibleColumns.monthlyTrend && (
                    <th className={`px-2 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <button
                        onClick={() => toggleColumnVisibility('monthlyTrend')}
                        className="text-green-500 hover:text-green-700 font-bold"
                        title="Tendance"
                      >
                        +
                      </button>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className={`${darkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}`}>
                {filteredUserStats.map((stat) => (
                  <tr key={stat.userId}>
                    <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <div className="text-sm font-medium">{stat.userName}</div>
                    </td>
                    {visibleColumns.email && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        <div className="text-sm">{stat.email}</div>
                      </td>
                    )}
                    {!visibleColumns.email && <td className="px-2 py-4"></td>}
                    {visibleColumns.offersCollected && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm">{stat.offersCollected}</div>
                      </td>
                    )}
                    {!visibleColumns.offersCollected && <td className="px-2 py-4"></td>}
                    {visibleColumns.offersPlaced && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm">{stat.offersPlaced}</div>
                      </td>
                    )}
                    {!visibleColumns.offersPlaced && <td className="px-2 py-4"></td>}
                    {visibleColumns.jobboardTransmissions && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-cyan-600">{stat.jobboardTransmissions}</div>
                      </td>
                    )}
                    {!visibleColumns.jobboardTransmissions && <td className="px-2 py-4"></td>}
                    {visibleColumns.prospectionTransmissions && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-sky-600">{stat.prospectionTransmissions}</div>
                      </td>
                    )}
                    {!visibleColumns.prospectionTransmissions && <td className="px-2 py-4"></td>}
                    {visibleColumns.directCVTransmissions && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-emerald-600">{stat.directCVTransmissions}</div>
                      </td>
                    )}
                    {!visibleColumns.directCVTransmissions && <td className="px-2 py-4"></td>}
                    {visibleColumns.pmsmpOffers && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-purple-600">{stat.pmsmpOffers}</div>
                      </td>
                    )}
                    {!visibleColumns.pmsmpOffers && <td className="px-2 py-4"></td>}
                    {visibleColumns.newCompanies && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm">{stat.newCompanies}</div>
                      </td>
                    )}
                    {!visibleColumns.newCompanies && <td className="px-2 py-4"></td>}
                    {visibleColumns.eventsCreated && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm">{stat.eventsCreated}</div>
                      </td>
                    )}
                    {!visibleColumns.eventsCreated && <td className="px-2 py-4"></td>}
                    {visibleColumns.eventProposals && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm">{stat.eventProposals}</div>
                      </td>
                    )}
                    {!visibleColumns.eventProposals && <td className="px-2 py-4"></td>}
                    {visibleColumns.beneficiairesAdded && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm font-semibold text-anthea-blue">{stat.beneficiairesAdded}</div>
                      </td>
                    )}
                    {!visibleColumns.beneficiairesAdded && <td className="px-2 py-4"></td>}
                    {visibleColumns.employedBeneficiaires && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm">
                          {stat.employedBeneficiaires} / {stat.totalBeneficiaires}
                          <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            ({stat.totalBeneficiaires > 0
                              ? Math.round((stat.employedBeneficiaires / stat.totalBeneficiaires) * 100)
                              : 0}%)
                          </span>
                        </div>
                      </td>
                    )}
                    {!visibleColumns.employedBeneficiaires && <td className="px-2 py-4"></td>}
                    {visibleColumns.placementRate && (
                      <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="text-sm">{stat.placementRate}%</div>
                      </td>
                    )}
                    {!visibleColumns.placementRate && <td className="px-2 py-4"></td>}
                    {visibleColumns.monthlyTrend && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {stat.monthlyTrend === 'up' ? (
                          <ArrowUp className="w-5 h-5 text-green-600" />
                        ) : stat.monthlyTrend === 'down' ? (
                          <ArrowDown className="w-5 h-5 text-red-600" />
                        ) : (
                          <div className="w-5 h-0.5 bg-gray-300 rounded-full" />
                        )}
                      </td>
                    )}
                    {!visibleColumns.monthlyTrend && <td className="px-2 py-4"></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      {/* Statistiques par utilisateur - Version mobile */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden md:hidden`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Users className="w-5 h-5 mr-2 text-anthea-blue" />
              Performance par utilisateur
            </h2>
            <button
              onClick={() => setShowUserStats(!showUserStats)}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
                showUserStats
                  ? darkMode ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
                  : darkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              title={showUserStats ? 'Masquer' : 'Afficher'}
            >
              <span className="text-xl font-bold">{showUserStats ? '-' : '+'}</span>
            </button>
          </div>

          {showUserStats && (
          <div className="space-y-4">
            {filteredUserStats.map((stat) => (
              <div key={stat.userId} className={`border ${darkMode ? 'border-gray-700' : 'border-gray-200'} rounded-lg overflow-hidden`}>
                <div
                  className={`flex items-center justify-between p-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} cursor-pointer`}
                  onClick={() => toggleUserExpand(stat.userId)}
                >
                  <div>
                    <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stat.userName}</div>
                    <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{stat.email}</div>
                  </div>
                  <div className="flex items-center">
                    <div className="mr-3 text-sm font-medium">
                      <span className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{stat.placementRate}%</span>
                    </div>
                    {expandedUser === stat.userId ? (
                      <ChevronDown className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-400'}`} />
                    ) : (
                      <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-400'}`} />
                    )}
                  </div>
                </div>

                {expandedUser === stat.userId && (
                  <div className="p-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`${darkMode ? 'bg-blue-900' : 'bg-blue-50'} rounded-lg p-2`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Offres collectées</p>
                            <p className={`text-lg font-bold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>{stat.offersCollected}</p>
                          </div>
                          <Briefcase className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-400'}`} />
                        </div>
                      </div>
                      <div className={`${darkMode ? 'bg-green-900' : 'bg-green-50'} rounded-lg p-2`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>Offres pourvues</p>
                            <p className={`text-lg font-bold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{stat.offersPlaced}</p>
                          </div>
                          <TrendingUp className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-400'}`} />
                        </div>
                      </div>
                      <div className={`${darkMode ? 'bg-purple-900' : 'bg-purple-50'} rounded-lg p-2`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>PMSMP</p>
                            <p className={`text-lg font-bold ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>{stat.pmsmpOffers}</p>
                          </div>
                          <Briefcase className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-400'}`} />
                        </div>
                      </div>
                      <div className={`${darkMode ? 'bg-yellow-900' : 'bg-yellow-50'} rounded-lg p-2`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>Entreprises</p>
                            <p className={`text-lg font-bold ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>{stat.newCompanies}</p>
                          </div>
                          <Building2 className={`w-5 h-5 ${darkMode ? 'text-yellow-400' : 'text-yellow-400'}`} />
                        </div>
                      </div>
                      <div className={`${darkMode ? 'bg-orange-900' : 'bg-orange-50'} rounded-lg p-2`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-orange-300' : 'text-orange-600'}`}>Événements</p>
                            <p className={`text-lg font-bold ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>{stat.eventsCreated}</p>
                          </div>
                          <Calendar className={`w-5 h-5 ${darkMode ? 'text-orange-400' : 'text-orange-400'}`} />
                        </div>
                      </div>
                      <div className={`${darkMode ? 'bg-pink-900' : 'bg-pink-50'} rounded-lg p-2`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-pink-300' : 'text-pink-600'}`}>Invitations</p>
                            <p className={`text-lg font-bold ${darkMode ? 'text-pink-300' : 'text-pink-700'}`}>{stat.eventProposals}</p>
                          </div>
                          <Users className={`w-5 h-5 ${darkMode ? 'text-pink-400' : 'text-pink-400'}`} />
                        </div>
                      </div>
                      <div className={`${darkMode ? 'bg-indigo-900' : 'bg-indigo-50'} rounded-lg p-2`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>Bénéficiaires</p>
                            <p className={`text-lg font-bold ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{stat.beneficiairesAdded}</p>
                          </div>
                          <Users className={`w-5 h-5 ${darkMode ? 'text-indigo-400' : 'text-indigo-400'}`} />
                        </div>
                      </div>
                    </div>

                    <div className={`mt-3 ${darkMode ? 'bg-teal-900' : 'bg-teal-50'} rounded-lg p-2`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-xs ${darkMode ? 'text-teal-300' : 'text-teal-600'}`}>Bénéficiaires en emploi</p>
                          <p className={`text-lg font-bold ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                            {stat.employedBeneficiaires} / {stat.totalBeneficiaires}
                            <span className="ml-2 text-xs font-normal">
                              ({stat.placementRate}%)
                            </span>
                          </p>
                        </div>
                        <UserCheck className={`w-5 h-5 ${darkMode ? 'text-teal-400' : 'text-teal-400'}`} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}